import { Badge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Link';
import { Device } from '@/generated/graphql';
import { DeviceConfig } from '@/models/device.models';
import { SiteConfig } from '@/models/site.models';
import {
  browserTimeZoneKey,
  defaultTimezoneKey,
  UTC_ZONE,
} from '@/models/time.models';
import {
  getDeviceState,
  renderDeviceStageBadge,
  renderDeviceStateBadge,
} from '@/utils/state';
import { createSortingButton } from '@/utils/table.function';
import { ColumnDef, RowData } from '@tanstack/react-table';
import { Navigation } from 'lucide-react';
import { DateTime } from 'luxon';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    exportValue?: (original: TData) => string;
  }
}

function getTimezoneFromKey(
  timezoneKey: string,
  defaultTimezone?: string,
): string {
  if (timezoneKey === browserTimeZoneKey) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } else if (defaultTimezone && timezoneKey === defaultTimezoneKey) {
    return defaultTimezone;
  } else return timezoneKey;
}

type DeviceTableMetadata = {
  extraData: {
    timezone: string;
  };
};

export const deviceColumns: ColumnDef<Device>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => createSortingButton(column, 'Name'),
    meta: { exportValue: (d) => d.name ?? '' },
    size: 175,
    enableSorting: true,
  },

  {
    accessorKey: 'serial',
    header: ({ column }) => createSortingButton(column, 'Serial'),
    meta: { exportValue: (d) => d.serial ?? '' },
    size: 175,
    enableSorting: true,
  },

  {
    accessorKey: 'product',
    header: ({ column }) => createSortingButton(column, 'Product'),
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {Array.isArray(row.original.products) &&
          row.original.products.sort().map((product) => (
            <Badge
              variant="outline"
              className="capitalize"
              key={`${row.original.id}_product_${product}`}
            >
              {product}
            </Badge>
          ))}
      </div>
    ),
    meta: {
      exportValue: (d) => ((d.products ?? []) as string[]).sort().join(', '),
    },
    size: 100,
    enableSorting: true,
  },

  {
    accessorKey: 'site',
    header: ({ column }) => createSortingButton(column, 'Site'),
    cell: ({ row }) => row.original.site?.name,
    meta: { exportValue: (d) => d.site?.name ?? '' },
    size: 250,
    enableSorting: true,
  },

  {
    accessorKey: 'customer',
    header: ({ column }) => createSortingButton(column, 'Customer'),
    cell: ({ row }) => row.original.site?.customer?.name,
    meta: { exportValue: (d) => d.site?.customer?.name ?? '' },
    size: 150,
    enableSorting: true,
  },

  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) =>
      renderDeviceStateBadge((row?.original?.config as DeviceConfig)?.state),
    meta: {
      exportValue: (d) => getDeviceState((d.config as DeviceConfig)?.state),
    },
    enableSorting: false,
    size: 110,
  },

  {
    accessorKey: 'environment',
    header: 'Environment',
    cell: ({ row }) =>
      renderDeviceStageBadge((row?.original?.config as DeviceConfig)?.state),
    meta: {
      exportValue: (d) =>
        (d.config as DeviceConfig)?.state?.productionReady
          ? 'Production'
          : 'Staging',
    },
    enableSorting: false,
    size: 110,
  },

  {
    accessorKey: 'lastActive',
    header: ({ column }) => createSortingButton(column, 'Last Active'),
    cell: ({ row, table }) => {
      const selectedTimezone = (table.options.meta as DeviceTableMetadata)
        .extraData.timezone;
      const timestamp = row.original.lastMedia?.lastOriginalMediaTime;
      const deviceTimezone = (row.original.site?.config as SiteConfig)
        ?.Timezone;
      const timezone = getTimezoneFromKey(selectedTimezone, deviceTimezone);
      if (timestamp) {
        return DateTime.fromISO(timestamp, { zone: UTC_ZONE })
          .setZone(timezone)
          .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
      }
    },
    meta: {
      exportValue: (d) => {
        const timezone = (d.site?.config as SiteConfig)?.Timezone ?? UTC_ZONE;
        return d.lastMedia?.lastOriginalMediaTime
          ? DateTime.fromISO(d.lastMedia.lastOriginalMediaTime, {
              zone: UTC_ZONE,
            })
              .setZone(timezone)
              .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
          : '';
      },
    },
    size: 175,
    enableSorting: true,
  },

  {
    accessorKey: 'zone',
    header: 'Zone',
    cell: ({ row }) => {
      const zones = (row.original.rois ?? []).map((roi) => roi.zone?.key);
      return (
        <>
          {zones.map((z) => (
            <p key={`${row.original.id}_${z}`} className="truncate" title={z}>
              {z}
            </p>
          ))}
        </>
      );
    },
    meta: {
      exportValue: (d) =>
        (d.rois ?? [])
          .map((roi) => roi.zone?.key ?? '')
          .filter(Boolean)
          .join(', '),
    },
    size: 200,
    enableSorting: false,
  },

  {
    accessorKey: 'siteLocation',
    header: 'Site Location',
    cell: ({ row }) => {
      const geometry = row.original.site?.geometry as
        | { type: string; coordinates: [number, number] }
        | undefined
        | null;
      if (geometry?.type === 'Point' && geometry.coordinates) {
        const [lng, lat] = geometry.coordinates;
        return `${lat}, ${lng}`;
      }
      return null;
    },
    meta: {
      exportValue: (d) => {
        const geometry = d.site?.geometry as
          | { type: string; coordinates: [number, number] }
          | undefined
          | null;
        if (geometry?.type === 'Point' && geometry.coordinates) {
          const [lng, lat] = geometry.coordinates;
          return `${lat}, ${lng}`;
        }
        return '';
      },
    },
    size: 175,
    enableSorting: false,
  },

  {
    accessorKey: 'users',
    header: 'Assigned Users',
    cell: ({ row }) => {
      const users = (row.original.userDeviceLinks ?? [])
        .map((link) => link.aspNetUser?.userName)
        .filter((u): u is string => !!u);
      return (
        <div className="flex flex-col gap-0.5">
          {users.map((u) => (
            <span
              key={u}
              className="text-muted-foreground truncate text-sm"
              title={u}
            >
              {u}
            </span>
          ))}
        </div>
      );
    },
    meta: {
      exportValue: (d) =>
        (d.userDeviceLinks ?? [])
          .map((link) => link.aspNetUser?.userName ?? '')
          .filter(Boolean)
          .join(', '),
    },
    size: 200,
    enableSorting: false,
  },

  {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => (
      <ButtonLink
        variant="outline"
        size={'icon-sm'}
        className="ms-auto flex"
        to={`${row.original.id}`}
      >
        <Navigation />
      </ButtonLink>
    ),
    size: 50,
    enableSorting: false,
  },
];
